// SigmaStudioControlCharts.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import {
  VictoryChart,
  VictoryLine,
  VictoryAxis,
  VictoryScatter,
  VictoryLabel,
} from "victory";
import './SigmaStudio.css';

const CHART_TYPES = {
  "I-MR":   { name: "I-MR (Individuals + Moving Range)", desc: "Single measurements over time" },
  "Xbar-R": { name: "Xbar-R (Average + Range)", desc: "Small groups (2–10 pieces)" },
  "p":      { name: "p-Chart (Proportion Defective)", desc: "% defective (variable sample)" },
  "np":     { name: "np-Chart (Number Defective)", desc: "Count defective (fixed sample)" },
  "c":      { name: "c-Chart (Defect Count)", desc: "Total defects per item" },
  "u":      { name: "u-Chart (Defects per Unit)", desc: "Defects per meter/kg (variable)" },
};

const CONTROL_LIMIT_CONSTANTS = {
  A2: { 2: 1.880, 3: 1.023, 4: 0.729, 5: 0.577, 6: 0.483, 7: 0.419, 8: 0.373, 9: 0.337, 10: 0.308 },
  D3: { 2: 0,     3: 0,     4: 0,     5: 0,     6: 0,     7: 0.076, 8: 0.136, 9: 0.184, 10: 0.223 },
  D4: { 2: 3.267, 3: 2.575, 4: 2.282, 5: 2.115, 6: 2.004, 7: 1.924, 8: 1.864, 9: 1.816, 10: 1.777 },
  E2: { 2: 2.659 },
};

const SigmaStudioControlCharts = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("I-MR");

  const [title, setTitle]                 = useState("");
  const [remarks, setRemarks]             = useState("");
  const [rawDataInput, setRawDataInput]   = useState("");
  const [sampleSizeInput, setSampleSizeInput] = useState("");

  const [showHelp, setShowHelp] = useState(false);
  const [chartResult, setChartResult] = useState(null);
  const [error, setError] = useState("");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const populateExample = () => {
    setError("");
    setChartResult(null);

    const examples = {
      "I-MR": {
        title: "Label Print Registration Deviation (mm)",
        remarks: "Single daily measurement of registration error from target position.",
        data: "0.04, -0.02, 0.07, 0.01, 0.05, -0.03, 0.08, 0.02, -0.04, 0.06, 0.00, 0.09, -0.01, 0.03, 0.05",
        sizes: ""
      },
      "Xbar-R": {
        title: "Label Cut Length – Subgroup of 5 labels",
        remarks: "Every hour measure 5 consecutive labels, record lengths in mm.",
        data: "50.12,50.08,50.15,50.10,50.05\n50.03,49.98,50.07,50.11,50.00\n50.20,50.14,50.09,50.16,50.12\n50.07,50.04,50.11,50.09,50.06",
        sizes: "5"
      },
      "p": {
        title: "% Defective Labels per Batch",
        remarks: "Random inspection of labels – record number defective and batch size.",
        data: "3,1,4,0,2,5,2,1",
        sizes: "120,85,150,100,130,110,95,105"
      },
      "np": {
        title: "Number of Bad Labels per Fixed Batch of 100",
        remarks: "Fixed sample of 100 labels checked each shift.",
        data: "4,2,5,1,3,0,6,2",
        sizes: "100"
      },
      "c": {
        title: "Total Defects per Printed Roll",
        remarks: "Count all printing, smearing, cutting defects on one complete roll.",
        data: "7,12,5,9,3,8,4,6",
        sizes: ""
      },
      "u": {
        title: "Defects per Meter of Printed Web",
        remarks: "Defects counted over different lengths of printed material.",
        data: "2.1,1.4,3.2,0.8,1.9,2.5,1.7,2.0",
        sizes: "50,30,80,40,60,55,45,65"
      }
    };

    const ex = examples[selectedType] || examples["I-MR"];
    setTitle(ex.title);
    setRemarks(ex.remarks);
    setRawDataInput(ex.data);
    setSampleSizeInput(ex.sizes);
  };

  const parseNumbers = (str) => {
    return str.split(/[\s,;\n]+/)
      .map(v => v.trim())
      .filter(v => v !== "" && !isNaN(Number(v)))
      .map(Number);
  };

  const detectOutOfControl = (points, center, ucl, lcl = 0) => {
    const signals = [];
    points.forEach((p, i) => {
      if (p.y > ucl || p.y < lcl) {
        signals.push({ point: p.x, rule: "Point outside control limits (±3σ)" });
      }
    });

    let runAbove = 0, runBelow = 0;
    points.forEach(p => {
      if (p.y > center) { runAbove++; runBelow = 0; }
      else if (p.y < center) { runBelow++; runAbove = 0; }
      else { runAbove = runBelow = 0; }

      if (runAbove >= 8) signals.push({ point: p.x, rule: "8+ points in a row above center" });
      if (runBelow >= 8) signals.push({ point: p.x, rule: "8+ points in a row below center" });
    });

    return signals.length ? signals : null;
  };

  const calculateChart = () => {
    setError("");
    setChartResult(null);

    if (!title.trim()) return setError("Please enter a title / process name.");

    const data = parseNumbers(rawDataInput);
    const sizes = parseNumbers(sampleSizeInput);

    if (data.length < 5) return setError("Please enter at least 5 valid numeric values.");

    let result = {
      title,
      remarks,
      type: selectedType,
      signals: null,
    };

    if (selectedType === "I-MR") {
      const mr = [];
      for (let i = 1; i < data.length; i++) mr.push(Math.abs(data[i] - data[i - 1]));
      const mrBar = mr.length ? mr.reduce((a, b) => a + b, 0) / mr.length : 0;
      const xBar = data.reduce((a, b) => a + b, 0) / data.length;
      const uclX = xBar + 2.659 * mrBar;
      const lclX = xBar - 2.659 * mrBar;
      const uclMR = 3.267 * mrBar;
      const lclMR = 0;

      result.individuals = { data: data.map((y, i) => ({ x: i + 1, y })), center: xBar, ucl: uclX, lcl: lclX };
      result.movingRange = { data: mr.map((y, i) => ({ x: i + 2, y })), center: mrBar, ucl: uclMR, lcl: lclMR };
      result.signals = detectOutOfControl(result.individuals.data, xBar, uclX, lclX);
    } else if (selectedType === "Xbar-R") {
      const subgroupSize = Number(sampleSizeInput) || 5;
      if (data.length % subgroupSize !== 0) return setError(`Data count (${data.length}) must be divisible by subgroup size (${subgroupSize}).`);

      const averages = [], ranges = [];
      for (let i = 0; i < data.length; i += subgroupSize) {
        const sub = data.slice(i, i + subgroupSize);
        const avg = sub.reduce((a, b) => a + b, 0) / subgroupSize;
        const rng = Math.max(...sub) - Math.min(...sub);
        averages.push(avg);
        ranges.push(rng);
      }

      const xBarBar = averages.reduce((a, b) => a + b, 0) / averages.length;
      const rBar = ranges.reduce((a, b) => a + b, 0) / ranges.length;
      const A2 = CONTROL_LIMIT_CONSTANTS.A2[subgroupSize] || 0.577;
      const D3 = CONTROL_LIMIT_CONSTANTS.D3[subgroupSize] || 0;
      const D4 = CONTROL_LIMIT_CONSTANTS.D4[subgroupSize] || 2.114;

      result.xbar = { data: averages.map((y, i) => ({ x: i + 1, y })), center: xBarBar, ucl: xBarBar + A2 * rBar, lcl: xBarBar - A2 * rBar };
      result.range = { data: ranges.map((y, i) => ({ x: i + 1, y })), center: rBar, ucl: D4 * rBar, lcl: D3 * rBar };
      result.signals = detectOutOfControl(result.xbar.data, xBarBar, result.xbar.ucl, result.xbar.lcl);
    } else if (["p", "np", "c", "u"].includes(selectedType)) {
      let cl, ucl, lcl, plotData;

      if (selectedType === "p" || selectedType === "np") {
        if (sizes.length !== data.length) return setError("Number of defectives and sample sizes must match.");
        let totalDef = 0, totalN = 0;
        data.forEach((d, i) => { totalDef += d; totalN += sizes[i]; });
        const pBar = totalDef / totalN;
        cl = pBar;
        const avgSize = sizes.reduce((a, b) => a + b, 0) / sizes.length;
        const std = Math.sqrt(pBar * (1 - pBar) / avgSize);
        ucl = pBar + 3 * std;
        lcl = Math.max(0, pBar - 3 * std);
        plotData = data.map((y, i) => ({ x: i + 1, y: selectedType === "p" ? y / sizes[i] : y }));
      } else if (selectedType === "c") {
        cl = data.reduce((a, b) => a + b, 0) / data.length;
        const std = Math.sqrt(cl);
        ucl = cl + 3 * std;
        lcl = Math.max(0, cl - 3 * std);
        plotData = data.map((y, i) => ({ x: i + 1, y }));
      } else if (selectedType === "u") {
        if (sizes.length !== data.length) return setError("Defects and units/sizes must match.");
        let totalDef = 0, totalUnits = 0;
        data.forEach((d, i) => { totalDef += d; totalUnits += sizes[i]; });
        const uBar = totalDef / totalUnits;
        cl = uBar;
        const avgSize = totalUnits / data.length;
        const std = Math.sqrt(uBar / avgSize);
        ucl = uBar + 3 * std;
        lcl = Math.max(0, uBar - 3 * std);
        plotData = data.map((y, i) => ({ x: i + 1, y: y / sizes[i] }));
      }

      result.data = plotData;
      result.center = cl;
      result.ucl = ucl;
      result.lcl = lcl;
      result.signals = detectOutOfControl(plotData, cl, ucl, lcl);
    }

    setChartResult(result);
  };

  const renderChart = (chartData, chartTitle, center, ucl, lcl, color = "#63b3ed") => (
    <div className="mb-10">
      <h3 className="text-lg text-cyan-300 mb-2 text-center">{chartTitle}</h3>
      <div style={{ height: "340px", width: "100%" }}>
        <VictoryChart
          height={340}
          padding={{ top: 50, bottom: 60, left: 70, right: 40 }}
          domainPadding={{ y: 30 }}
        >
          <VictoryAxis
            label="Observation / Sample"
            style={{
              axis: { stroke: "#718096" },
              tickLabels: { fill: "#e2e8f0", fontSize: 10 },
              axisLabel: { fill: "#a0aec0", fontSize: 12, padding: 40 },
            }}
          />
          <VictoryAxis
            dependentAxis
            label="Value"
            style={{
              axis: { stroke: "#718096" },
              tickLabels: { fill: "#e2e8f0", fontSize: 10 },
              axisLabel: { fill: "#a0aec0", fontSize: 12, angle: -90, padding: 50 },
            }}
          />

          <VictoryLine
            data={[{ x: 1, y: center }, { x: chartData.length, y: center }]}
            style={{ data: { stroke: "#48bb78", strokeWidth: 2.5 } }}
          />
          <VictoryLine
            data={[{ x: 1, y: ucl }, { x: chartData.length, y: ucl }]}
            style={{ data: { stroke: "#f56565", strokeDasharray: "6,4" } }}
          />
          <VictoryLine
            data={[{ x: 1, y: lcl }, { x: chartData.length, y: lcl }]}
            style={{ data: { stroke: "#f56565", strokeDasharray: "6,4" } }}
          />

          <VictoryScatter
            data={chartData}
            size={5}
            style={{ data: { fill: color, stroke: "#2c5282", strokeWidth: 1.5 } }}
          />

          <VictoryLabel
            text={`UCL = ${ucl.toFixed(4)}`}
            x={80}
            y={40}
            style={{ fill: "#f56565", fontSize: 11 }}
          />
          <VictoryLabel
            text={`Center = ${center.toFixed(4)}`}
            x={80}
            y={80}
            style={{ fill: "#48bb78", fontSize: 11 }}
          />
          <VictoryLabel
            text={`LCL = ${lcl.toFixed(4)}`}
            x={80}
            y={120}
            style={{ fill: "#f56565", fontSize: 11 }}
          />
        </VictoryChart>
      </div>
    </div>
  );

  const renderHelp = () => (
    <div className="mt-6 p-6 bg-gray-900 rounded-xl border border-teal-900/50">
      <h2 className="text-xl lg:text-2xl font-bold text-yellow-300 mb-6 text-center">
        Help – Understanding Control Charts (Simple Guide for Everyone)
      </h2>

      <div className="space-y-8 text-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-teal-300">I-MR Chart (Individuals + Moving Range)</h3>
          <p>You measure one thing at a time (example: registration error once per shift). The chart shows each measurement and how much it changes from the previous one. Good for daily checks when you can't measure many pieces at once.</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-teal-300">Xbar-R Chart (Average & Range)</h3>
          <p>You measure a small group (like 5 labels every hour). The chart shows the average of the group and the biggest-smallest difference (range). Very useful for length, weight, thickness in printing.</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-teal-300">p-Chart & np-Chart</h3>
          <p>For "good or bad" inspection. p = percentage bad, np = number bad (when sample size is same). Example: how many labels have printing defects out of 100 checked.</p>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-teal-300">c-Chart & u-Chart</h3>
          <p>For counting actual defects (misprints, spots, scratches). c = total defects per roll, u = defects per meter. Perfect for variable-length printed material.</p>
        </div>

        <div className="bg-gray-800 p-5 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-orange-300 mb-3">When the Process is "Out of Control" (Red Flags)</h3>
          <ul className="list-disc pl-6 space-y-2">
            <li>Any point outside the red dashed lines (too high or too low)</li>
            <li>8 or more points continuously above or below the green center line</li>
            <li>Sudden big jumps or strange repeating patterns</li>
          </ul>
          <p className="mt-4 text-sm italic">
            These signs mean something unusual happened — check machine settings, ink, paper, operator change, etc. Fix early to save money and avoid waste!
          </p>
        </div>
      </div>

      <div className="text-center mt-8">
        <button
          onClick={() => setShowHelp(false)}
          className="w-40 px-2 py-2 bg-teal-700 hover:bg-teal-600 rounded-lg font-medium"
        >
          Close Help Section
        </button>
      </div>
    </div>
  );

  const [showAddRow, setShowAddRow] = useState(false);
  const [newValue, setNewValue] = useState("");

  return (
    <div className="min-h-screen flex flex-col bg-black [&_p]:text-white [&_li]:text-gray-100 text-gray-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            📈 Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">Control Charts – Simple Statistical Process Control for All MSMEs</div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button
                onClick={handleLogout}
                className="px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 text-sm"
              >
                ⏻ Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="px-3 py-1 rounded border border-gray-600 text-sm"
            >
              🔐 Login
            </button>
          )}
        </div>

        <button
          className="md:hidden text-2xl text-teal-300"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-700 px-4 py-4">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="px-6 py-2 bg-red-700 rounded">
                Logout
              </button>
            </div>
          ) : (
            <div className="text-center">
              <button onClick={() => navigate("/login")} className="px-6 py-2 border rounded">
                Login
              </button>
            </div>
          )}
        </div>
      )}

      <main className="flex-1 p-4 pb-12 max-w-6xl mx-auto">
        <div className="grid grid-cols-3 gap-2 place-items-center mb-2">
          <button
            onClick={populateExample}
            className="text-sm px-2 py-2 bg-green-700 hover:bg-green-600 rounded-md font-medium"
          >
            Populate Example ({selectedType})
          </button>
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="text-sm px-2 py-2 bg-blue-700 hover:bg-blue-600 rounded-md font-medium"
          >
            {showHelp ? "Hide" : "Show"} Help – How to Use
          </button>
           <button
            onClick={() => setShowAddRow(true)}
            className="text-sm px-2 py-2 bg-blue-700 hover:bg-blue-600 rounded-md font-medium"
          >
            Add Data Point
          </button>
        </div>

        {showAddRow && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-900 p-6 rounded-lg border border-gray-700 w-full max-w-md">
              <h3 className="text-lg text-teal-300 mb-4">Add New Measurement</h3>
              <input
                type="number"
                step="any"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded mb-4 text-white"
                placeholder="e.g. 0.05 or 50.12"
                value={newValue}
                onChange={e => setNewValue(e.target.value)}
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowAddRow(false)}
                  className="px-4 py-2 bg-gray-700 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (newValue.trim() && !isNaN(Number(newValue))) {
                      setRawDataInput(prev => prev ? prev + ", " + newValue : newValue);
                      setNewValue("");
                      setShowAddRow(false);
                    }
                  }}
                  className="px-4 py-2 bg-teal-700 rounded"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {showHelp && renderHelp()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Select Control Chart Type</label>
            <select
              className="w-full h-12 p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              {Object.entries(CHART_TYPES).map(([key, { name, desc }]) => (
                <option key={key} value={key}>
                  {name} — {desc}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Process / Chart Title</label>
            <input
              type="text"
              className="w-full p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Daily Label Registration Accuracy Check"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-cyan-300 font-medium">Remarks / Notes</label>
          <textarea
            className="w-full h-24 p-3 bg-gray-900 border border-gray-700 rounded text-white"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Machine number, shift, special observations, actions taken..."
          />
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-cyan-300 font-medium">
            Data Values
            {selectedType === "Xbar-R" ? " (one subgroup per line or comma separated)" : ""}
          </label>
          <textarea
            className="w-full h-40 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
            value={rawDataInput}
            onChange={(e) => setRawDataInput(e.target.value)}
            placeholder={
              selectedType === "Xbar-R"
                ? "50.12,50.08,50.15,50.10,50.05\n50.03,49.98,50.07,50.11,50.00\n..."
                : "0.04, -0.02, 0.07, 0.01, 0.05, ..."
            }
          />
        </div>

        {(selectedType === "Xbar-R" || ["p", "np", "u"].includes(selectedType)) && (
          <div className="mb-6">
            <label className="block mb-2 text-cyan-300 font-medium">
              Sample / Subgroup Sizes
              {selectedType === "Xbar-R" ? " (fixed number e.g. 5)" : " (one per data group)"}
            </label>
            <input
              type="text"
              className="w-full p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={sampleSizeInput}
              onChange={(e) => setSampleSizeInput(e.target.value)}
              placeholder={selectedType === "Xbar-R" ? "5" : "120,85,150,100,..."}
            />
          </div>
        )}

        <div className="flex justify-center mb-8">
          <button
            onClick={calculateChart}
            className="w-48 px-2 py-2 bg-purple-700 hover:bg-purple-600 rounded-lg font-medium text-sm disabled:opacity-50"
            disabled={!title.trim() || !rawDataInput.trim()}
          >
            Generate Control Chart
          </button>
        </div>

        {error && (
          <div className="p-4 mb-6 bg-red-900/70 border border-red-700 rounded text-red-100 text-center">
            {error}
          </div>
        )}

        {chartResult && (
          <div className="mt-8 p-6 bg-gray-950 border border-gray-700 rounded-xl">
            <h2 className="text-2xl text-teal-300 mb-4 text-center">{chartResult.title}</h2>
            {chartResult.remarks && (
              <p className="text-gray-300 mb-6 text-center italic">{chartResult.remarks}</p>
            )}

            {chartResult.signals && (
              <div className="mb-8 p-5 bg-red-950/60 border border-red-700 rounded-lg">
                <h3 className="text-xl text-red-300 mb-3">⚠️ Out-of-Control Signals Detected</h3>
                <ul className="list-disc pl-6 space-y-2 text-red-100">
                  {chartResult.signals.map((s, idx) => (
                    <li key={idx}>
                      Point {s.point}: {s.rule}
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-red-200">
                  Investigate special cause — machine, material, operator, environment change.
                </p>
              </div>
            )}

            {chartResult.individuals &&
              renderChart(chartResult.individuals.data, "Individuals Chart", chartResult.individuals.center, chartResult.individuals.ucl, chartResult.individuals.lcl)}

            {chartResult.movingRange &&
              renderChart(chartResult.movingRange.data, "Moving Range Chart", chartResult.movingRange.center, chartResult.movingRange.ucl, chartResult.movingRange.lcl, "#fbbf24")}

            {chartResult.xbar &&
              renderChart(chartResult.xbar.data, "Xbar Chart (Averages)", chartResult.xbar.center, chartResult.xbar.ucl, chartResult.xbar.lcl)}

            {chartResult.range &&
              renderChart(chartResult.range.data, "Range Chart", chartResult.range.center, chartResult.range.ucl, chartResult.range.lcl, "#fbbf24")}

            {chartResult.data &&
              renderChart(chartResult.data, `${selectedType.toUpperCase()} Chart`, chartResult.center, chartResult.ucl, chartResult.lcl)}
          </div>
        )}
      </main>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-auto">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <div className="text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions
          </div>
          <div className="flex flex-wrap gap-6">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SigmaStudioControlCharts;